import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { router } from "expo-router";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image } from "expo-image";

import UnlockUsersBaseCard from "../../Base/UnlockUsersBaseCard";

import {
  PUBLIC_PHOTO_REQUIREMENT,
  ProfileCompletionScore,
  getPublicPhotosScore,
} from "@/utils/unlockingStage";
import { UserProfile } from "@/schemas/user/profile";
import { PaginatedResponse } from "@/schemas/api/pagination";
import { UserLikableImageMedia } from "@/schemas/user/image";

type Props = {
  userProfile: UserProfile;
  onAction: () => void;
};

function UnlockUsersPublicPhotosCard({
  userProfile,
  onAction,
}: Props): React.JSX.Element {
  const minPhotoCount = PUBLIC_PHOTO_REQUIREMENT.MIN_COUNT;

  const publicPhotosCount = userProfile.publicImages.total;
  const isCompleted = publicPhotosCount >= minPhotoCount;
  const score = getPublicPhotosScore(userProfile);

  function getLeftComponent(
    publicImages: PaginatedResponse<UserLikableImageMedia>,
  ): React.JSX.Element {
    if (publicPhotosCount === 0 || publicImages.items[0] === undefined) {
      return (
        <MaterialCommunityIcons
          name="account-box-multiple-outline"
          size={36}
          color="black"
        />
      );
    }

    const numberOfImagesToShow = Math.min(publicPhotosCount, minPhotoCount, 4);

    return (
      <View>
        {numberOfImagesToShow === 1 ? (
          <View style={styles.rowCenter}>
            <Image
              key={publicImages.items[0].image.id}
              source={{
                uri: publicImages.items[0].image.attributes.previewUrl,
              }}
              style={styles.imageLarge}
              contentFit="cover"
              cachePolicy="memory-disk"
            />
          </View>
        ) : numberOfImagesToShow === 3 ? (
          <>
            <View style={styles.rowCenter}>
              <Image
                key={publicImages.items[0].image.id}
                source={{
                  uri: publicImages.items[0].image.attributes.previewUrl,
                }}
                style={styles.imageSmall}
                contentFit="cover"
                cachePolicy="memory-disk"
              />
            </View>
            <View style={styles.rowEven}>
              {publicImages.items.slice(1, 3).map((item) => (
                <Image
                  key={item.image.id}
                  source={{ uri: item.image.attributes.previewUrl }}
                  style={styles.imageSmall}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                />
              ))}
            </View>
          </>
        ) : (
          <View style={styles.gridContainer}>
            {publicImages.items.slice(0, numberOfImagesToShow).map((item) => (
              <Image
                key={item.image.id}
                source={{ uri: item.image.attributes.previewUrl }}
                style={styles.imageSmall}
                contentFit="cover"
                cachePolicy="memory-disk"
              />
            ))}
          </View>
        )}
      </View>
    );
  }

  return (
    <UnlockUsersBaseCard
      iconComponent={getLeftComponent(userProfile.publicImages)}
      title="Public Photos"
      description={`Add at least ${minPhotoCount} public photos`}
      totalScore={ProfileCompletionScore.PUBLIC_PHOTOS}
      earnedScore={score}
      statusComponent={
        <Text>
          {isCompleted
            ? "Completed"
            : `${Math.min(publicPhotosCount, minPhotoCount)}/${minPhotoCount} added`}
        </Text>
      }
      isCompleted={isCompleted}
      onPressActionButton={() => {
        onAction();
        router.push("/(main)/media/home");
      }}
    />
  );
}

const styles = StyleSheet.create({
  imageSmall: {
    width: 18,
    height: 18,
    borderRadius: 2,
    margin: 1,
  },
  imageLarge: {
    width: 36,
    height: 36,
    borderRadius: 4,
    margin: 1,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
  },
  rowCenter: {
    flexDirection: "row",
    justifyContent: "center",
  },
  rowEven: {
    flexDirection: "row",
    justifyContent: "center",
  },
});

export default UnlockUsersPublicPhotosCard;
